import type { Role } from '@conversia/shared-types';
import { api } from './client';

/** Roles de departamento del negocio activo (estándar Codeus). Prefijo: /api/roles */

export type Accion = 'ver' | 'crear' | 'editar' | 'borrar' | 'aprobar';

export interface RolDeNegocio {
  id: string;
  nombre: string;
  descripcion: string | null;
  permisos: Record<string, Accion[]>;
  /** Campos que no ve en ninguna de sus áreas. */
  camposOcultos: string[];
  topes: Record<string, number>;
}

export interface PantallaDeRoles {
  acciones: Accion[];
  areas: Array<{ id: string; nombre: string }>;
  campos: Array<{ id: string; nombre: string }>;
  /** `otrasAreas`: rutas de ese tope que se aprueban en otra área (bajar el catálogo: Productos). */
  topes: Array<{ id: string; area: string; nombre: string; otrasAreas?: string[] }>;
  roles: RolDeNegocio[];
  personas: Array<{
    id: string; name: string | null; email: string; role: Role; isActive: boolean; lastLoginAt: string | null; rolDeNegocioId: string | null;
    /** Trabaja también en otro negocio de la cuenta: su baja la da el dueño del sistema. */
    otrosNegocios: boolean;
  }>;
  invitaciones: InvitacionPendiente[];
  /** El sistema puede mandar correos (invitaciones y enlaces para restablecer la clave). */
  correoActivo: boolean;
  /** Quien mira entra por su rol de departamento (no es administrador). */
  porRolDeDepartamento: boolean;
  /** Los roles que quien mira puede dar: los que caben en el suyo. */
  asignables: string[];
}

export interface InvitacionPendiente {
  id: string;
  email: string;
  role: Role;
  rolDeNegocioId: string | null;
  expiresAt: string;
  createdAt: string;
  /** Solo para un administrador: el enlace es una llave para crear esa cuenta. */
  enlace: string | null;
}

export interface EntradaDeActividad {
  id: string;
  action: string;
  entityType: string;
  entityId: string;
  metadataJson: Record<string, unknown>;
  ip: string | null;
  createdAt: string;
  user: { name: string | null; email: string } | null;
}

export interface SesionAbierta {
  id: string;
  userAgent: string | null;
  ip: string | null;
  createdAt: string;
  expiresAt: string;
  user: { id: string; name: string | null; email: string };
}

export type RolNuevo = Omit<RolDeNegocio, 'id'>;

export const getPantalla = () => api.get<PantallaDeRoles>('/api/roles');
export const crear = (rol: RolNuevo) => api.post<RolDeNegocio>('/api/roles', rol);
export const editar = (id: string, rol: RolNuevo) => api.patch<RolDeNegocio>(`/api/roles/${id}`, rol);
export const borrar = (id: string) => api.del<{ success: boolean; personasSinRol: number }>(`/api/roles/${id}`);
export const asignar = (userId: string, rolDeNegocioId: string | null) =>
  api.put<{ userId: string; rolDeNegocioId: string | null }>(`/api/roles/persona/${userId}`, { rolDeNegocioId });
export const getSesiones = () => api.get<SesionAbierta[]>('/api/roles/sesiones');
export const cerrarSesion = (id: string) => api.post<{ success: boolean }>(`/api/roles/sesiones/${id}/cerrar`, {});

export const invitar = (input: { email: string; role: Role; rolDeNegocioId: string | null }) =>
  api.post<InvitacionPendiente & { correoEnviado: boolean }>('/api/roles/invitaciones', input);
export const revocarInvitacion = (id: string) => api.del<{ success: boolean }>(`/api/roles/invitaciones/${id}`);
export const darDeBaja = (userId: string) => api.post<{ userId: string; isActive: boolean }>(`/api/roles/persona/${userId}/desactivar`, {});
export const reactivar = (userId: string) => api.post<{ userId: string; isActive: boolean }>(`/api/roles/persona/${userId}/reactivar`, {});
export const restablecerClave = (userId: string) => api.post<{ enviadoA: string }>(`/api/roles/persona/${userId}/restablecer-clave`, {});
export const getActividad = () => api.get<EntradaDeActividad[]>('/api/roles/actividad');
